import { useEffect } from "react";

const SITE_URL = "https://booksailingmumbai.co.in";
const SITE_NAME = "Book Sailing Mumbai";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

// Marks every tag this hook owns, so a later route can overwrite the previous
// route's values instead of stacking duplicates in <head>.
const OWNED = "data-seo-managed";

/**
 * @param {string} selector
 * @param {() => HTMLElement} create
 * @returns {HTMLElement}
 */
function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute(OWNED, "true");
    document.head.appendChild(el);
  }
  return el;
}

function setMetaByName(name, content) {
  if (!content) return;
  const el = upsert(`meta[name="${name}"]`, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("name", name);
    return meta;
  });
  el.setAttribute("content", content);
}

function setMetaByProperty(property, content) {
  if (!content) return;
  const el = upsert(`meta[property="${property}"]`, () => {
    const meta = document.createElement("meta");
    meta.setAttribute("property", property);
    return meta;
  });
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  const el = upsert('link[rel="canonical"]', () => {
    const link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    return link;
  });
  el.setAttribute("href", href);
}

/**
 * Per-route <head> management for this CRA single-page app.
 *
 * These tags are written by JavaScript, so scripts/prerender.js snapshots
 * every route after build — crawlers that don't run JS (most AI/answer-engine
 * bots) get the finished <head> and page content as static HTML.
 *
 * @param {{
 *   title: string,
 *   description?: string,
 *   canonical?: string,
 *   image?: string,
 *   type?: string,
 *   jsonLd?: object | null,
 *   noindex?: boolean
 * }} options
 */
export default function useSeo({
  title,
  description,
  canonical,
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd = null,
  noindex = false,
}) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    if (title) document.title = title;

    setMetaByName("description", description);
    setMetaByName("robots", noindex ? "noindex, follow" : "index, follow");
    setCanonical(canonical);

    setMetaByProperty("og:site_name", SITE_NAME);
    setMetaByProperty("og:locale", "en_IN");
    setMetaByProperty("og:type", type);
    setMetaByProperty("og:title", title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", canonical || SITE_URL);
    setMetaByProperty("og:image", image);

    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", title);
    setMetaByName("twitter:description", description);
    setMetaByName("twitter:image", image);
  }, [title, description, canonical, image, type, noindex]);

  useEffect(() => {
    // A prerendered page already carries this route's JSON-LD in its HTML;
    // drop it so the live copy below doesn't duplicate it.
    document.head
      .querySelectorAll(`script[${OWNED}="route"]`)
      .forEach((el) => el.remove());

    if (!jsonLdKey) return undefined;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute(OWNED, "route");
    script.textContent = jsonLdKey;
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [jsonLdKey]);
}

export { SITE_URL, SITE_NAME };
